"use client";

import { useRouter } from "next/navigation";
import { useState } from "react";

const apiBase = process.env.NEXT_PUBLIC_API_BASE_URL || "/api";

type AuthModalProps = {
  onClose: () => void;
};

export default function AuthModal({ onClose }: AuthModalProps) {
  const router = useRouter();
  const [mode, setMode] = useState<"login" | "magic" | "forgot">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const handleMagicLink = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setSuccessMessage(null);
    setIsSending(true);
    try {
      const res = await fetch(`${apiBase}/auth/request-link`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(`Request failed: ${res.status} ${errorText}`);
      }
      router.push("/check-email");
    } catch (err) {
      console.error("Auth form error:", err);
      setError(err instanceof Error ? err.message : "Request failed");
    } finally {
      setIsSending(false);
    }
  };

  const handleForgotPassword = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setSuccessMessage(null);
    setIsSending(true);
    try {
      const res = await fetch(`${apiBase}/auth/request-password-reset`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(`Request failed: ${res.status} ${errorText}`);
      }
      setSuccessMessage("If an account exists with that email, you'll receive a password reset link.");
    } catch (err) {
      console.error("Password reset error:", err);
      setError(err instanceof Error ? err.message : "Request failed");
    } finally {
      setIsSending(false);
    }
  };

  const handleLogin = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setSuccessMessage(null);
    setIsSending(true);
    try {
      const res = await fetch(`${apiBase}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, password }),
      });
      
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || `Login failed: ${res.status}`);
      }

      // Login successful, redirect to dashboard
      onClose();
      window.location.href = "/dashboard";
    } catch (err) {
      console.error("Login error:", err);
      setError(err instanceof Error ? err.message : "Login failed");
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="relative w-full max-w-md mx-4 rounded-2xl border border-white/10 bg-[#0A101A] p-8 shadow-2xl">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-white/50 hover:text-white transition-colors"
          aria-label="Close"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <h2 className="text-2xl font-semibold text-white mb-2">
          {mode === "forgot" ? "Reset password" : "Welcome back"}
        </h2>
        <p className="text-sm text-white/60 mb-6">
          {mode === "login" 
            ? "Sign in with your credentials" 
            : mode === "magic"
            ? "Enter your email to receive a magic link"
            : "Enter your email to receive a password reset link"}
        </p>

        {/* Toggle buttons - only show when not in forgot mode */}
        {mode !== "forgot" && (
          <div className="flex gap-2 mb-6">
            <button
              type="button"
              onClick={() => setMode("login")}
              className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition ${
                mode === "login"
                  ? "bg-[#14FF76] text-[#050B14]"
                  : "bg-white/5 text-white/60 hover:text-white hover:bg-white/10"
              }`}
            >
              Password
            </button>
            <button
              type="button"
              onClick={() => setMode("magic")}
              className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition ${
                mode === "magic"
                  ? "bg-[#14FF76] text-[#050B14]"
                  : "bg-white/5 text-white/60 hover:text-white hover:bg-white/10"
              }`}
            >
              Magic Link
            </button>
          </div>
        )}

        {mode === "login" ? (
          <form onSubmit={handleLogin} className="space-y-4">
            <label className="block text-sm text-white/70">
              Email address
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-[#14FF76]/60"
                placeholder="you@example.com"
              />
            </label>

            <label className="block text-sm text-white/70">
              Password
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-[#14FF76]/60"
                placeholder="••••••••"
              />
            </label>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => {
                  setMode("forgot");
                  setError(null);
                  setSuccessMessage(null);
                }}
                className="text-xs text-[#14FF76] hover:text-[#00C24E] transition-colors"
              >
                Forgot password?
              </button>
            </div>

            {error && <p className="text-sm text-red-400">{error}</p>}

            <button
              type="submit"
              disabled={isSending}
              className="w-full rounded-lg bg-[#14FF76] px-4 py-2.5 text-sm font-semibold text-[#050B14] transition hover:bg-[#00C24E] disabled:opacity-50"
            >
              {isSending ? "Signing in..." : "Sign in"}
            </button>
          </form>
        ) : mode === "magic" ? (
          <form onSubmit={handleMagicLink} className="space-y-4">
            <label className="block text-sm text-white/70">
              Email address
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-[#14FF76]/60"
                placeholder="you@example.com"
              />
            </label>
            {error && <p className="text-sm text-red-400">{error}</p>}
            <button
              type="submit"
              disabled={isSending}
              className="w-full rounded-lg bg-[#14FF76] px-4 py-2.5 text-sm font-semibold text-[#050B14] transition hover:bg-[#00C24E] disabled:opacity-50"
            >
              {isSending ? "Sending..." : "Send magic link"}
            </button>
          </form>
        ) : (
          <form onSubmit={handleForgotPassword} className="space-y-4">
            <label className="block text-sm text-white/70">
              Email address
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-[#14FF76]/60"
                placeholder="you@example.com"
              />
            </label>
            {error && <p className="text-sm text-red-400">{error}</p>}
            {successMessage && <p className="text-sm text-[#14FF76]">{successMessage}</p>}
            <button
              type="submit"
              disabled={isSending}
              className="w-full rounded-lg bg-[#14FF76] px-4 py-2.5 text-sm font-semibold text-[#050B14] transition hover:bg-[#00C24E] disabled:opacity-50"
            >
              {isSending ? "Sending..." : "Send reset link"}
            </button>
            <button
              type="button"
              onClick={() => {
                setMode("login");
                setError(null);
                setSuccessMessage(null);
              }}
              className="w-full text-sm text-white/60 hover:text-white transition-colors"
            >
              Back to login
            </button>
          </form>
        )}

        <p className="mt-6 text-xs text-center text-white/50">
          Don't have an account?{" "}
          <a href="/signup" className="text-[#14FF76] hover:text-[#00C24E] transition-colors">
            Start your free trial
          </a>
        </p>
      </div>
    </div>
  );
}
